import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'

import {
  MerkletreeScanStatus,
  NETWORK_CONFIG,
  type MerkletreeScanUpdateEvent
} from '@railgun-community/shared-models'

import { ModeEnum } from '@web/contexts/appModeContext'
import useAppMode from '@web/hooks/useAppMode'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import { getNetworksForBalance } from '@web/utils/railgunCore'
import { railgunEmitter } from '@web/utils/railgunListener'

export type RailgunMerkletreeScanContextValue = {
  privateMerkletreeScanProgress: number
  isPrivateMerkletreeScanning: boolean
  privateMerkletreeScanProgressByChainId: Record<number, number>
  scanningProgress: string
}

const defaultMerkletreeScanValue: RailgunMerkletreeScanContextValue = {
  privateMerkletreeScanProgress: 0,
  isPrivateMerkletreeScanning: false,
  privateMerkletreeScanProgressByChainId: {},
  scanningProgress: '0%'
}

const RailgunMerkletreeScanContext = createContext<RailgunMerkletreeScanContextValue>(
  defaultMerkletreeScanValue
)

type TreeScanState = { progress: number; isScanning: boolean }
const SCAN_UPDATE_THROTTLE_MS = 250

function mapEventToTreeState(event: MerkletreeScanUpdateEvent): TreeScanState {
  switch (event.scanStatus) {
    case MerkletreeScanStatus.Complete:
      return { progress: 1, isScanning: false }
    case MerkletreeScanStatus.Incomplete:
      return { progress: 0, isScanning: false }
    default:
      return { progress: event.progress, isScanning: true }
  }
}

/**
 * Isolated from RailgunContext so merkletree scan ticks do not recreate the main wallet context value
 * or re-render RailgunContextProvider.
 */
export const RailgunMerkletreeScanProvider: React.FC<{
  children: React.ReactNode
  railgunEngineInitialized: boolean
}> = ({ children, railgunEngineInitialized }) => {
  const { mode } = useAppMode()
  const isPrivateMode = mode === ModeEnum.PRIVATE
  const { account, dashboardNetworkFilter } = useSelectedAccountControllerState()
  const accountAddr = account?.addr

  const [utxoByChain, setUtxoByChain] = useState<Record<number, TreeScanState>>({})
  const [txidByChain, setTxidByChain] = useState<Record<number, TreeScanState>>({})
  const pendingUtxoByChainRef = useRef<Record<number, TreeScanState>>({})
  const pendingTxidByChainRef = useRef<Record<number, TreeScanState>>({})
  const utxoFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const txidFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Monotonic floor for the displayed scan %, keyed by scope (the active network filter). RAILGUN
  // emits per-chain phase resets (UTXO→TXID, tree boundaries) and transient Incomplete events; with
  // the SW now fanning chains out in parallel, several report progress at once, so a naive average
  // jitters (20→30→15→35→16). We pin the headline to its running max within a session and reset only
  // when the scope changes or a fresh scan restarts from ~0.
  const scanProgressFloorRef = useRef<{ scope: string; max: number }>({ scope: '', max: 0 })

  // Reset scan state only on a genuinely new wallet/engine — NOT on every
  // public↔private toggle. Clearing on `isPrivateMode` made the progress UI flash
  // 0→X on each re-entry to private even though the chain was already fully
  // scanned this session. Account change / engine re-init still reset it.
  useEffect(() => {
    setUtxoByChain({})
    setTxidByChain({})
    pendingUtxoByChainRef.current = {}
    pendingTxidByChainRef.current = {}
  }, [accountAddr, railgunEngineInitialized])

  useEffect(() => {
    if (!railgunEngineInitialized || !isPrivateMode) return

    const flushPendingUtxo = () => {
      const pending = pendingUtxoByChainRef.current
      pendingUtxoByChainRef.current = {}
      utxoFlushTimerRef.current = null
      if (!Object.keys(pending).length) return
      setUtxoByChain((prev) => {
        let hasAnyChange = false
        const next = { ...prev }
        for (const [idString, state] of Object.entries(pending)) {
          const id = Number(idString)
          const prevState = prev[id]
          if (
            prevState != null &&
            prevState.progress === state.progress &&
            prevState.isScanning === state.isScanning
          ) {
            continue
          }
          next[id] = state
          hasAnyChange = true
        }
        return hasAnyChange ? next : prev
      })
    }

    const flushPendingTxid = () => {
      const pending = pendingTxidByChainRef.current
      pendingTxidByChainRef.current = {}
      txidFlushTimerRef.current = null
      if (!Object.keys(pending).length) return
      setTxidByChain((prev) => {
        let hasAnyChange = false
        const next = { ...prev }
        for (const [idString, state] of Object.entries(pending)) {
          const id = Number(idString)
          const prevState = prev[id]
          if (
            prevState != null &&
            prevState.progress === state.progress &&
            prevState.isScanning === state.isScanning
          ) {
            continue
          }
          next[id] = state
          hasAnyChange = true
        }
        return hasAnyChange ? next : prev
      })
    }

    const onUtxo = (event: MerkletreeScanUpdateEvent) => {
      const chainId = event.chain.id
      const next = mapEventToTreeState(event)
      pendingUtxoByChainRef.current[chainId] = next
      if (!utxoFlushTimerRef.current) {
        utxoFlushTimerRef.current = setTimeout(flushPendingUtxo, SCAN_UPDATE_THROTTLE_MS)
      }
    }

    const onTxid = (event: MerkletreeScanUpdateEvent) => {
      const chainId = event.chain.id
      const next = mapEventToTreeState(event)
      pendingTxidByChainRef.current[chainId] = next
      if (!txidFlushTimerRef.current) {
        txidFlushTimerRef.current = setTimeout(flushPendingTxid, SCAN_UPDATE_THROTTLE_MS)
      }
    }

    railgunEmitter.on('utxoScanUpdate', onUtxo)
    railgunEmitter.on('txidScanUpdate', onTxid)

    return () => {
      railgunEmitter.off('utxoScanUpdate', onUtxo)
      railgunEmitter.off('txidScanUpdate', onTxid)
      if (utxoFlushTimerRef.current) {
        clearTimeout(utxoFlushTimerRef.current)
        utxoFlushTimerRef.current = null
      }
      if (txidFlushTimerRef.current) {
        clearTimeout(txidFlushTimerRef.current)
        txidFlushTimerRef.current = null
      }
      pendingUtxoByChainRef.current = {}
      pendingTxidByChainRef.current = {}
    }
  }, [railgunEngineInitialized, isPrivateMode])

  const targetChainIds = useMemo(() => {
    const names = getNetworksForBalance(dashboardNetworkFilter)
    return names.map((name) => NETWORK_CONFIG[name].chain.id)
  }, [dashboardNetworkFilter])

  const privateMerkletreeScanProgressByChainId = useMemo(() => {
    const out: Record<number, number> = {}
    if (!isPrivateMode || !railgunEngineInitialized) return out
    for (const id of targetChainIds) {
      const u = utxoByChain[id]?.progress ?? 0
      const t = txidByChain[id]?.progress ?? 0
      out[id] = (u + t) / 2
    }
    return out
  }, [isPrivateMode, railgunEngineInitialized, targetChainIds, utxoByChain, txidByChain])

  const privateMerkletreeScanProgress = useMemo(() => {
    if (!isPrivateMode || !railgunEngineInitialized || targetChainIds.length === 0) return 0
    const total = targetChainIds.reduce((sum, id) => {
      return sum + (privateMerkletreeScanProgressByChainId[id] ?? 0)
    }, 0)
    return total / targetChainIds.length
  }, [
    isPrivateMode,
    railgunEngineInitialized,
    targetChainIds,
    privateMerkletreeScanProgressByChainId
  ])

  const isPrivateMerkletreeScanning = useMemo(() => {
    if (!isPrivateMode || !railgunEngineInitialized) return false
    return targetChainIds.some((id) => {
      const u = utxoByChain[id]
      const t = txidByChain[id]
      return (u?.isScanning ?? false) || (t?.isScanning ?? false)
    })
  }, [isPrivateMode, railgunEngineInitialized, targetChainIds, utxoByChain, txidByChain])

  const scanningProgress = useMemo(() => {
    const useAggregate =
      dashboardNetworkFilter == null ||
      dashboardNetworkFilter === 'rewards' ||
      dashboardNetworkFilter === 'gasTank'
    let p: number
    if (useAggregate) {
      p = privateMerkletreeScanProgress
    } else {
      const id = Number(dashboardNetworkFilter)
      p = Number.isNaN(id) ? 0 : (privateMerkletreeScanProgressByChainId[id] ?? 0)
    }
    // Clamp to a monotonic floor so the displayed % only ever moves forward within a session.
    const scope = useAggregate ? 'all' : String(dashboardNetworkFilter)
    const floor = scanProgressFloorRef.current
    if (floor.scope !== scope) {
      floor.scope = scope
      floor.max = 0
    }
    if (p <= 0.001) {
      // Fresh scan (or nothing scanning yet): drop the floor so we don't pin at a stale 100%.
      floor.max = 0
    } else if (p < floor.max) {
      p = floor.max
    } else {
      floor.max = p
    }
    if (p) {
      return `${Math.floor(p * 100)}%`
    }
    return '0%'
  }, [
    dashboardNetworkFilter,
    privateMerkletreeScanProgress,
    privateMerkletreeScanProgressByChainId
  ])

  const value = useMemo(
    () => ({
      privateMerkletreeScanProgress,
      isPrivateMerkletreeScanning,
      privateMerkletreeScanProgressByChainId,
      scanningProgress
    }),
    [
      privateMerkletreeScanProgress,
      isPrivateMerkletreeScanning,
      privateMerkletreeScanProgressByChainId,
      scanningProgress
    ]
  )

  return (
    <RailgunMerkletreeScanContext.Provider value={value}>
      {children}
    </RailgunMerkletreeScanContext.Provider>
  )
}

export function useRailgunMerkletreeScanContext(): RailgunMerkletreeScanContextValue {
  return useContext(RailgunMerkletreeScanContext)
}
